import { Image } from "revolt-api";
import { API } from "revolt.js";
import styled from "styled-components";
import { useTriggerEvents } from "preact-context-menu";
import { useEffect, useState } from "preact/hooks";

import { Modal } from "@revoltchat/ui";

import AttachmentActions from "../../../components/common/messaging/attachments/AttachmentActions";
import EmbedMediaActions from "../../../components/common/messaging/embed/EmbedMediaActions";
import { useClient } from "../../client/ClientController";
import { modalController } from "../ModalController";
import { ImageLike, ModalProps } from "../types";

const Viewer = styled.div`
    display: flex;
    overflow: hidden;
    flex-direction: column;
    border-end-end-radius: 4px;
    border-end-start-radius: 4px;
    background-color: rgb(from var(--secondary-background) r g b / 80%);
`;

const ImageContainer = styled.div`
    width: 80dvw;
    height: 80dvh;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ViewerImage = styled.img`
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
`;

const PinContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    padding: 0.2em 1.2em;
`;

const Pin = styled.button`
    cursor: pointer;
    --pin-size: 14px;
    &[data-selected] {
        --pin-size: 18px;
    }

    border: none;
    background-color: var(--accent);

    width: var(--pin-size);
    height: var(--pin-size);
    border-radius: 50%;
    transition:
        width 0.625s ease-in-out,
        height 0.625s ease-in-out;
    display: flex;
    align-items: center;
    justify-content: center;
    /* border: 1px solid #000000bdfff; */
`;

type PinControllerProps = {
    onPinSelected: (index: number) => unknown;
    amount: number;
    currentPin: number;
};

function PinController({
    currentPin,
    amount,
    onPinSelected,
}: PinControllerProps) {
    const MAX_SIDE_PINS = 999;
    const rightPinCount = Math.min(MAX_SIDE_PINS, amount - (currentPin + 1));
    const rightPinIndexStart = currentPin + 1;

    let leftPinCount = currentPin;
    if (leftPinCount > MAX_SIDE_PINS) {
        leftPinCount = MAX_SIDE_PINS;
    }

    if (leftPinCount < 0) {
        leftPinCount = 0;
    }

    const leftPinIndexStart = currentPin - leftPinCount;

    function onPinSelectedInner(ev: MouseEvent) {
        const target = ev.currentTarget as HTMLButtonElement;
        const index = target.getAttribute("data-pin-index");
        if (index === null) {
            return;
        }
        const parsed = parseInt(index);
        if (isNaN(parsed)) {
            return;
        }

        onPinSelected(parsed);
    }

    return (
        <PinContainer>
            {leftPinCount > 0 && (
                <>
                    {produce(leftPinCount, (idx) => (
                        <Pin
                            key={leftPinIndexStart + idx}
                            data-pin-index={leftPinIndexStart + idx}
                            data-pin-order={leftPinIndexStart + idx + 1}
                            onClick={onPinSelectedInner}
                        />
                    ))}
                </>
            )}
            <Pin
                data-selected
                data-pin-index={currentPin}
                data-pin-order={currentPin + 1}
                key={currentPin}
            />
            {rightPinCount > 0 && (
                <>
                    {produce(rightPinCount, (idx) => (
                        <Pin
                            key={rightPinIndexStart + idx}
                            data-pin-order={rightPinIndexStart + idx + 1}
                            data-pin-index={rightPinIndexStart + idx}
                            onClick={onPinSelectedInner}
                        />
                    ))}
                </>
            )}
        </PinContainer>
    );
}

function produce<T>(count: number, supplier: (index: number) => T) {
    const items: T[] = [];

    for (let i = 0; i < count; i++) {
        items.push(supplier(i));
    }

    return items;
}

const enum Step {
    Left = -1,
    Right = 1,
}

const leftActionKeys = new Set(["ArrowLeft", "KeyA"]);
const rightActionKeys = new Set(["ArrowRight", "KeyD"]);

type SwipeCb = (to: "right" | "left") => unknown;

type DnDWrapperProps = {
    children?: Children;

    onSwipe?: SwipeCb;
};
function DnDWrapper({ children }: DnDWrapperProps) {
    return (
        <div
            draggable={false}
            style={{ touchAction: "none" }}
            onClick={() => modalController.close()}>
            {children}
        </div>
    );
}

export function ImageViewerGallery({
    images,
    startImage,
    ...props
}: ModalProps<"image_viewer_gallery">) {
    const [currentIndex, setCurrentIndex] = useState(startImage || 0);
    const currentImage = images[currentIndex];

    function stepBy(step: Step) {
        setCurrentIndex((idx) => {
            let result = idx + step;
            if (result < 0) {
                result = 0;
            }
            if (result > images.length - 1) {
                result = images.length - 1;
            }
            return result;
        });
    }

    useEffect(() => {
        const onKeyPress = (e: KeyboardEvent) => {
            const key = e.code;
            if (leftActionKeys.has(key)) {
                e.stopPropagation();
                e.preventDefault();
                stepBy(Step.Left);
            } else if (rightActionKeys.has(key)) {
                e.stopPropagation();
                e.preventDefault();
                stepBy(Step.Right);
            }
        };

        document.body.addEventListener("keydown", onKeyPress);

        return () => {
            document.body.removeEventListener("keydown", onKeyPress);
        };
    }, []);

    if (!currentImage) {
        return null;
    }

    return (
        <Modal
            {...props}
            fullscreen
            transparent
            maxHeight="100vh"
            maxWidth="100vw">
            <BasicViewerLayout
                image={currentImage}
                onSwipe={(swipe) => {
                    if (swipe === "left") {
                        stepBy(-1);
                    } else {
                        stepBy(1);
                    }
                }}
            />
            {images.length > 1 && (
                <PinController
                    amount={images.length}
                    currentPin={currentIndex}
                    onPinSelected={setCurrentIndex}
                />
            )}
        </Modal>
    );
}

export default function ImageViewer({
    image,
    ...props
}: ModalProps<"image_viewer">) {
    return (
        <Modal {...props} transparent maxHeight="100vh" maxWidth="100vw">
            <BasicViewerLayout image={image} />
        </Modal>
    );
}

type BasicViewerLayoutProps = {
    image: ImageLike;
    onSwipe?: SwipeCb;
};

function BasicViewerLayout({ image, onSwipe }: BasicViewerLayoutProps) {
    const client = useClient();

    let attachment: API.File | undefined;
    if (isAttachmentImage(image)) {
        attachment = image;
    }
    if (attachment && attachment.metadata.type !== "Image") {
        console.warn(
            `Attempted to use a non valid attachment type in the image viewer: ${attachment.metadata.type}`,
        );
        return null;
    }

    let embed: API.Image | undefined;
    if (isImage(image)) {
        embed = image;
    }

    return (
        <Viewer
            onWheel={(e: WheelEvent) => {
                if (e.deltaY > 0) {
                    onSwipe?.("right");
                } else {
                    onSwipe?.("left");
                }
            }}>
            {attachment && (
                <>
                    <DnDWrapper>
                        <ImageContainer>
                            <ViewerImage
                                draggable={false}
                                loading="eager"
                                src={client.generateFileURL(attachment)}
                                {...useTriggerEvents("Menu", {
                                    attachment,
                                })}
                            />
                        </ImageContainer>
                    </DnDWrapper>
                    <AttachmentActions attachment={attachment} />
                </>
            )}
            {embed && (
                <>
                    <DnDWrapper>
                        <ImageContainer>
                            <ViewerImage
                                draggable={false}
                                loading="eager"
                                src={client.proxyFile(embed.url)}
                            />
                        </ImageContainer>
                    </DnDWrapper>
                    <EmbedMediaActions embed={embed} />
                </>
            )}
        </Viewer>
    );
}

function isAttachmentImage(imageLike: ImageLike): imageLike is API.File {
    return !!(imageLike as API.File)?.metadata?.type;
}

function isImage(imageLike: ImageLike): imageLike is Image {
    return !!(imageLike as Image)?.url;
}
